import React, { useState, useEffect } from "react";
import axios from "axios";
import { Toast } from "primereact/toast";
import TranzPayPage from "./TranzPayPage";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate } from "../utils/dateFormat";

const STATE_ABBREVIATIONS: Record<string, string> = {
  Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
  Colorado: "CO", Connecticut: "CT", Delaware: "DE", Florida: "FL", Georgia: "GA",
  Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA",
  Kansas: "KS", Kentucky: "KY", Louisiana: "LA", Maine: "ME", Maryland: "MD",
  Massachusetts: "MA", Michigan: "MI", Minnesota: "MN", Mississippi: "MS", Missouri: "MO",
  Montana: "MT", Nebraska: "NE", Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ",
  "New Mexico": "NM", "New York": "NY", "North Carolina": "NC", "North Dakota": "ND",
  Ohio: "OH", Oklahoma: "OK", Oregon: "OR", Pennsylvania: "PA", "Rhode Island": "RI",
  "South Carolina": "SC", "South Dakota": "SD", Tennessee: "TN", Texas: "TX", Utah: "UT",
  Vermont: "VT", Virginia: "VA", Washington: "WA", "West Virginia": "WV",
  Wisconsin: "WI", Wyoming: "WY",
};

interface PaymentData {
  data?: {
    paymentLink?: string;
    receiptDatabaseId?: string;
  };
}

interface PaymentScreenProps {
  onSubmit?: (data: {
    totalPremium: string;
    downpayment: string;
    monthlyPayment: string;
    firstPaymentDate: string;
    totalDownpaymentDue: number;
  }) => void;
  onPrevious?: () => void;
  onNext?: (data?: any) => void;
  insuredId: string;
}

export default function PaymentScreen({
  onSubmit,
  onPrevious,
  onNext,
  workflowData,
  insuredId,
}: PaymentScreenProps & { workflowData?: any; insuredId: string }) {
  let isAncillaryFlow = workflowData && workflowData[`step1`] && workflowData[`step1`].ancillaryFlow === true;
  if (!isAncillaryFlow && typeof window !== "undefined") {
    isAncillaryFlow = localStorage.getItem("ancillaryFlow") === "true";
  }
  const [totalPremium, setTotalPremium] = useState("");
  const [downpayment, setDownpayment] = useState("");
  const [monthlyPayment, setMonthlyPayment] = useState("");
  const [firstPaymentDate, setFirstPaymentDate] = useState("");
  const [agencyFee, setAgencyFee] = useState(isAncillaryFlow ? 0 : (workflowData?.step7?.agencyFee || 0));
  const transpayFee = workflowData?.step7?.tranzpayServiceFee || 0;
  // Calculate ancillary premiums based on selected ancillaries and quote data
  const [ancillaryPremiums, setAncillaryPremiums] = useState(0);
  const [selectedQuote, setSelectedQuote] = useState<any>(null);
  const [globalAncillaries, setGlobalAncillaries] = useState<any>(workflowData?.step7?.globalAncillaries || {});
  // Helper to calculate ancillary price for only the four Way products
  const searchParams = new URLSearchParams(window.location.search);

  const calculateAncillaryPrice = (key: string, vehicleCount: number) => {
    if (!globalAncillaries[key]) return 0;
    switch (key) {
      case "wayRTG6Mon":
        return vehicleCount > 0 ? 99 + (vehicleCount - 1) * 50 : 0;
      case "wayRTG3Mon":
        return vehicleCount > 0 ? 69 + (vehicleCount - 1) * 35 : 0;
      case "wayRoadside6Mon":
        return vehicleCount > 0 ? 79 + (vehicleCount - 1) * 25 : 0;
      case "wayRoadside3Mon":
        return vehicleCount > 0 ? 59 + (vehicleCount - 1) * 25 : 0;
      default:
        return 0;
    }
  };

  // Calculate sum of selected Way ancillaries only
  useEffect(() => {
    let vehicleCount = 1;
    if (isAncillaryFlow && workflowData?.step7 && typeof workflowData.step7.vehicleCountFromApi === "number") {
      vehicleCount = workflowData.step7.vehicleCountFromApi;
    } else if (workflowData?.step3 && Array.isArray(workflowData.step3)) {
      vehicleCount = workflowData.step3.length || 1;
    }
    let sum = 0;
    ["wayRTG6Mon", "wayRTG3Mon", "wayRoadside6Mon", "wayRoadside3Mon"].forEach((key) => {
      sum += calculateAncillaryPrice(key, vehicleCount);
    });
    setAncillaryPremiums(sum);
  }, [workflowData, isAncillaryFlow]);

  const totalDownpaymentDue = (isAncillaryFlow ? 0 : downpayment ? parseFloat(downpayment) : 0) + agencyFee + transpayFee + ancillaryPremiums;

  const toast = React.useRef<Toast>(null);

  // const [cardNumber, setCardNumber] = useState("");
  // const [cardExpiry, setCardExpiry] = useState("");
  // const [cardCVV, setCardCVV] = useState("");
  // const [cardName, setCardName] = useState("");
  const [showPayment, setShowPayment] = useState(false);
  const [paymentLink, setPaymentLink] = useState("");
  const [receiptId, setReceiptId] = useState("");
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    totalPremium?: string;
    downpayment?: string;
    monthlyPayment?: string;
    firstPaymentDate?: string;
  }>({});

  useEffect(() => {
    if (workflowData.step8?.paymentCompleted) {
      setShowPayment(true);
      setPaymentLink(workflowData.step8.paymentLink || "");
      setReceiptId(workflowData.step8.receiptId || "");
    } else if (workflowData.step8?.paymentData?.data) {
      setShowPayment(true);
      setPaymentLink(workflowData.step8.paymentData?.data.paymentLink || "");
      setReceiptId(workflowData.step8.paymentData?.data.receiptDatabaseId || "");
    }
  }, [workflowData]);

  useEffect(() => {
    const quoteNumber = workflowData?.storedQuoteNumber;
    if (quoteNumber) {
      axios
        .get(`${process.env.REACT_APP_ACE_API_Url}/api/ACE/getquotepaymentbyquotenumber/${quoteNumber}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          },
        })
        .then((response: any) => {
          const data = response.data;
          if (data) {
            if (data.paymentDataValues) {
              const values = JSON.parse(data.paymentDataValues);
              setTotalPremium(values.TotalPremium?.toString() || "");
              setDownpayment(values.DownPayment || "");
              setMonthlyPayment(values.MonthlyPayment || "");
              setFirstPaymentDate(values.DateofFirstPaymentDue || "");
              if (values.AgencyFee !== undefined) setAgencyFee(isAncillaryFlow ? 0 : values.AgencyFee);
              if (values.AncillaryPremiums !== undefined) setAncillaryPremiums(values.AncillaryPremiums);
            }
            if (data.paymentDataJson) {
              const paymentData: PaymentData = JSON.parse(data.paymentDataJson);
              if (paymentData.data && paymentData.data.paymentLink) {
                setShowPayment(true);
                setPaymentLink(paymentData.data.paymentLink || "");
                setReceiptId(paymentData.data.receiptDatabaseId || "");
              }
            }
          }
        })
        .catch((error: any) => {
          console.error("Error fetching payment data:", error);
        });
    }
  }, [workflowData]);

  const handlePaymentComplete = async () => {
    const paymentData = {
      totalPremium,
      downpayment,
      monthlyPayment,
      firstPaymentDate,
      totalDownpaymentDue,
      paymentCompleted: true,
      receiptId,
      paymentLink,
    };
    const quoteId = workflowData?.quoteId || (workflowData?.step7?.selectedQuotes ? workflowData?.step7?.selectedQuotes : undefined);
    try {
      await axios.post(
        `${process.env.REACT_APP_ACE_API_Url}/api/ACE/insertwayQuote?ncToken=${localStorage.getItem("NCToken")}&transactionId=${searchParams.get("transactionId")}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
          },
        },
      );

      if (onSubmit) {
        onSubmit({ ...paymentData });
      }
      if (onNext) {
        onNext({ ...paymentData, quoteId });
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Failed to insert way quotes.",
        life: 3000,
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: {
      totalPremium?: string;
      downpayment?: string;
      monthlyPayment?: string;
      firstPaymentDate?: string;
    } = {};

    if (!isAncillaryFlow) {
      if (!totalPremium || parseFloat(totalPremium) <= 0) {
        errors.totalPremium = "Total premium is required and must be greater than 0";
      }

      if (!downpayment || parseFloat(downpayment) <= 0) {
        errors.downpayment = "Down payment is required and must be greater than 0";
      }

      if (!monthlyPayment || parseFloat(monthlyPayment) <= 0) {
        errors.monthlyPayment = "Monthly payment is required and must be greater than 0";
      }

      if (!firstPaymentDate) {
        errors.firstPaymentDate = "First payment date is required";
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Validate form before processing
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    const paymentData = {
      totalPremium,
      downpayment,
      monthlyPayment,
      firstPaymentDate,
      totalDownpaymentDue,
    };

    // Use selectedQuotes[0] for ancillary flow, fallback to quoteId otherwise
    let actualQuoteId = workflowData?.quoteId || (workflowData?.step7?.selectedQuotes ? workflowData?.step7?.selectedQuotes : undefined);
    if (!actualQuoteId) {
      if (isAncillaryFlow) {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          life: 3000,
        });
      } else {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          life: 3000,
        });
      }
      setLoading(false);
      return;
    }

    proceedWithPayment(actualQuoteId);
  };

  const proceedWithPayment = (actualQuoteId: string) => {

    const transactionDate = new Date().toISOString();
    const dueDate = firstPaymentDate ? new Date(firstPaymentDate).toISOString() : transactionDate;
    const paymentReceiptDate = transactionDate;

    const payload = {
      ncToken: localStorage.getItem("NCToken") || "",
      payload: {
        insuredDatabaseId: insuredId,
        transactionDate,
        dueDate,
        paymentReceiptDate,
        agentId: localStorage.getItem("NowcertsAgentId") || "",
        fees: [
          {
            feeName: "Down Payment",
            dueAmount: parseFloat(downpayment) || 0,
          },
          {
            feeName: "Agency Fee",
            dueAmount: agencyFee,
          },
          {
            feeName: "Ancillary Premiums",
            dueAmount: ancillaryPremiums,
          },
        ],
        quoteIds: [actualQuoteId],
      },
    };

    axios
      .post(`${process.env.REACT_APP_ACE_API_Url}/api/ace/insertquickpayment`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
        },
      })
      .then((response: any) => {
        setPaymentLink(response.data.data.paymentLink);
        setReceiptId(response.data.data.receiptDatabaseId);
        setShowPayment(true);

        const savePayload = {
          quoteId: actualQuoteId,
          quoteNumber: workflowData?.storedQuoteNumber || "",
          transactionId: searchParams.get("transactionId") || "",
          paymentReceiptId: response.data.data.receiptDatabaseId,
          paymentStatus: "Done",
          paymentDataJson: JSON.stringify(response.data),
          currentStep: "7",
          paymentDataValues: {
            TotalPremium: parseFloat(totalPremium) || 0,
            DownPayment: downpayment,
            AgencyFee: agencyFee,
            AncillaryPremiums: ancillaryPremiums,
            TranzPayServiceFee: transpayFee,
            generatedAt: new Date().toISOString(),
            MonthlyPayment: monthlyPayment,
            DateofFirstPaymentDue: firstPaymentDate,
            TotalDueToday: totalDownpaymentDue,
          },
        };

        axios
          .post(`${process.env.REACT_APP_ACE_API_Url}/api/ACE/addorupdatequotepayment`, savePayload, {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
            },
          })
          .then((saveResponse: any) => {
            setLoading(false);

            // Only insert quote for full flow, not ancillary flow
            if (!isAncillaryFlow) {
              const insertData = JSON.stringify({
                databaseId: actualQuoteId,
                insuredDatabaseId: insuredId,
                insuredFirstName: workflowData?.step1?.firstName || "",
                insuredLastName: workflowData?.step1?.lastName || "",
                insuredCommercialName: workflowData?.step1?.commercialName || "",
                insuredEmail: workflowData?.step1?.email || "",
                effectiveDate: (() => {
                  const raw = workflowData?.step4?.policyEffectiveDate;
                  if (!raw) return "";
                  const toMMDDYYYY = (d: Date) => {
                    const mm = String(d.getMonth() + 1).padStart(2, "0");
                    const dd = String(d.getDate()).padStart(2, "0");
                    const yyyy = d.getFullYear();
                    return `${mm}/${dd}/${yyyy}`;
                  };
                  const date = new Date(raw);
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return date < today ? toMMDDYYYY(today) : toMMDDYYYY(date);
                })(),
                expirationDate: workflowData?.step4?.policyExpirationDate || "",
                bindDate: "",
                lineOfBusinessName: "Personal Auto",
                premium: totalPremium,
                carrierName: workflowData?.step4?.carrier || "",
                quoteNumber: workflowData?.storedQuoteNumber || "",
                agency_fee: agencyFee,
                stateAbbreviationCode: STATE_ABBREVIATIONS[workflowData?.step1?.state || ""] || workflowData?.step1?.state || "",
                billingType:"Direct_Bill_with_Agency_Down_Payment"
              });

              axios
                .post(`${process.env.REACT_APP_ACE_API_Url}/api/ace/insertquote?ncToken=${localStorage.getItem("NCToken")}`, insertData, {
                  headers: {
                    Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
                    "Content-Type": "application/json",
                  },
                })
                .then((insertResponse: any) => {
                  const insertedQuoteId = insertResponse.data?.quoteId;
                  if (insertedQuoteId) {
                    axios
                      .post(
                        `${process.env.REACT_APP_ACE_API_Url}/api/ace/generatetransaction?policyId=${insertedQuoteId}&ncToken=${localStorage.getItem("NCToken")}`,
                        {},
                        {
                          headers: {
                            Authorization: `Bearer ${localStorage.getItem("igtoken")}`,
                          },
                        },
                      )
                      .catch((genError: any) => {
                        console.error("Error generating transaction:", genError);
                      });
                  }
                  toast.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Quote updated successfully!",
                    life: 3000,
                  });
                })
                .catch((insertError: any) => {
                  toast.current?.show({
                    severity: "error",
                    summary: "Error",
                    detail: "Error updating quote!",
                    life: 3000,
                  });
                });
            } else {
              // For ancillary flow, just show success message
              toast.current?.show({
                severity: "success",
                summary: "Success",
                detail: "Payment processed successfully!",
                life: 3000,
              });
            }
          })
          .catch((saveError: any) => {
            console.error("Error saving payment:", saveError);
            setLoading(false);
          });
      })
      .catch((error: any) => {
        console.error("Error calling payment API:", error);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: "Error processing payment. Please try again.",
          life: 3000,
        });
        setLoading(false);
        setShowPayment(true);
      });
  };

  return showPayment ? (
    <>
      <Toast ref={toast} />
      <TranzPayPage paymentLink={paymentLink} onPaymentComplete={handlePaymentComplete} receiptId={receiptId} onPrevious={onPrevious} />
    </>
  ) : (
    <>
      <Toast ref={toast} />
      <div className="page-container">
        <div className="page-header mb-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Payment Information</h1>
              <p className="text-sm text-gray-500">Enter and review payment details</p>
            </div>
          </div>
        </div>
        <div className="form-grid">
          <form id="payment-form" className="payment-form" onSubmit={handleSubmit}>
            <div className="form-section-header form-group full-width">
              <h3 className="text-lg font-semibold mb-2">Payment Details</h3>
            </div>
            <div className="flex flex-wrap gap-6">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="font-medium mb-1">
                  Total Auto Prem (excl fees &amp; taxes)
                  {!isAncillaryFlow && <span className="text-danger"> *</span>}
                </label>
                <input
                  type="number"
                  className={`form-input compact-input ${validationErrors.totalPremium ? "error" : ""}`}
                  placeholder="Enter total premium"
                  value={totalPremium}
                  onChange={(e) => {
                    setTotalPremium(e.target.value);
                    if (validationErrors.totalPremium) {
                      setValidationErrors((prev) => ({ ...prev, totalPremium: undefined }));
                    }
                  }}
                  min="0"
                  step="0.01"
                />
                {validationErrors.totalPremium && <span className="error-message">{validationErrors.totalPremium}</span>}
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="font-medium mb-1">
                  Down Payment
                  {!isAncillaryFlow && <span className="text-danger"> *</span>}
                </label>
                <input
                  type="number"
                  className={`form-input compact-input ${validationErrors.downpayment ? "error" : ""}`}
                  placeholder="Enter down payment"
                  value={downpayment}
                  onChange={(e) => {
                    setDownpayment(e.target.value);
                    if (validationErrors.downpayment) {
                      setValidationErrors((prev) => ({ ...prev, downpayment: undefined }));
                    }
                  }}
                  min="0"
                  step="0.01"
                />
                {validationErrors.downpayment && <span className="error-message">{validationErrors.downpayment}</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-6 mt-4">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="font-medium mb-1">Agency Fee</label>
                <input
                  type="number"
                  className="form-input compact-input"
                  placeholder="Enter agency fee"
                  value={agencyFee}
                  onChange={(e) => setAgencyFee(parseFloat(e.target.value) || 0)}
                  min="0"
                  step="0.01"
                  disabled
                />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="font-medium mb-1">TranzPay Service Fee</label>
                <input type="number" className="form-input compact-input" value={transpayFee} readOnly disabled />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="font-medium mb-1">Ancillary Premiums</label>
                <input
                  type="number"
                  className="form-input compact-input"
                  placeholder="Ancillary premiums"
                  value={ancillaryPremiums}
                  readOnly
                  disabled
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-6 mt-4">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="font-medium mb-1">
                  Monthly Payment
                  {!isAncillaryFlow && <span className="text-danger"> *</span>}
                </label>
                <input
                  type="number"
                  className={`form-input compact-input ${validationErrors.monthlyPayment ? "error" : ""}`}
                  placeholder="Enter monthly payment"
                  value={monthlyPayment}
                  onChange={(e) => {
                    setMonthlyPayment(e.target.value);
                    if (validationErrors.monthlyPayment) {
                      setValidationErrors((prev) => ({ ...prev, monthlyPayment: undefined }));
                    }
                  }}
                  min="0"
                  step="0.01"
                />
                {validationErrors.monthlyPayment && <span className="error-message">{validationErrors.monthlyPayment}</span>}
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="font-medium mb-1">
                  Date of First Payment Due
                  {!isAncillaryFlow && <span className="text-danger"> *</span>}
                </label>
                <DatePicker
                  selected={firstPaymentDate ? new Date(firstPaymentDate) : null}
                  onChange={(date: Date | null) => {
                    setFirstPaymentDate(date ? formatDate(date) : "");
                    if (validationErrors.firstPaymentDate) {
                      setValidationErrors((prev) => ({ ...prev, firstPaymentDate: undefined }));
                    }
                  }}
                  dateFormat="MM/dd/yyyy"
                  placeholderText="MM/DD/YYYY"
                  className={`form-input compact-input ${validationErrors.firstPaymentDate ? "error" : ""}`}
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={10}
                  minDate={new Date()}
                  autoComplete="off"
                />
                {validationErrors.firstPaymentDate && <span className="error-message">{validationErrors.firstPaymentDate}</span>}
              </div>
            </div>
            <div className="form-group mt-4">
              <label className="font-medium mb-1">Total Due Today</label>
              <input type="number" className="form-input readonly-input compact-input" value={totalDownpaymentDue} readOnly />
            </div>
            {/* <div className="form-section-header form-group full-width mt-8">
            <h3 className="text-lg font-semibold mb-2">Credit Card Information</h3>
          </div>
          <div className="flex flex-wrap gap-6">
            <div className="form-group" style={{ flex: 1 }}>
              <label className="font-medium mb-1">Cardholder Name</label>
              <input
                type="text"
                className="form-input compact-input"
                placeholder="Name on card"
                value={cardName}
                onChange={e => setCardName(e.target.value)}
                
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="font-medium mb-1">Card Number</label>
              <input
                type="text"
                className="form-input compact-input"
                placeholder="Card number"
                value={cardNumber}
                onChange={e => setCardNumber(e.target.value)}
                maxLength={19}
                
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="font-medium mb-1">Expiry (MM/YY)</label>
              <input
                type="text"
                className="form-input compact-input"
                placeholder="MM/YY"
                value={cardExpiry}
                onChange={e => setCardExpiry(e.target.value)}
                maxLength={5}
                
              />
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label className="font-medium mb-1">CVV</label>
              <input
                type="password"
                className="form-input compact-input"
                placeholder="CVV"
                value={cardCVV}
                onChange={e => setCardCVV(e.target.value)}
                maxLength={4}
                
              />
            </div>
          </div> */}
            <div className="form-actions flex justify-between mt-8">
              <button className="btn btn-secondary" onClick={onPrevious} disabled={loading}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" stroke="currentColor" />
                </svg>
                Previous
              </button>
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Saving..." : "Continue"}
                {!loading && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3.5" d="M9 5l7 7-7 7" stroke="currentColor" />
                  </svg>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}
